import { Decoration, ViewPlugin } from "@codemirror/view";
import { syntaxTree } from "@codemirror/language";
import { parseColorArguments } from "./utils/tree-operations/colors.js";
import { centeringNodeForEnvironment } from "./utils/tree-operations/figure.js";
import { getUnstarredEnvironmentName } from "./utils/tree-operations/environments.js";
import { parseTheoremStyles } from "./utils/tree-operations/theorems.js";

export const markDecorations = ViewPlugin.define(
    (view) => {
        const createDecorations = (state, tree) => {
            const decorations = [];
            const theoremStyles = parseTheoremStyles(state, tree);

            for (const { from, to } of view.visibleRanges) {
                tree.iterate({
                    from,
                    to,
                    enter(nodeRef) {
                        if (nodeRef.type.is("KnownCommand") || nodeRef.type.is("UnknownCommand")) {
                            const ctrlSeq =
                                nodeRef.node.getChild("$CtrlSeq") ?? nodeRef.node.firstChild?.getChild("$CtrlSeq");

                            if (ctrlSeq) {
                                const text = state.doc.sliceString(ctrlSeq.from + 1, ctrlSeq.to);
                                if (text === "label") {
                                    decorations.push(
                                        Decoration.mark({
                                            class: `ol-cm-${text}`,
                                            inclusive: true,
                                        }).range(nodeRef.from, nodeRef.to),
                                    );
                                } else {
                                    const markFrom = ctrlSeq.to + 1;
                                    const markTo = nodeRef.to - 1;
                                    if (markTo > markFrom) {
                                        decorations.push(
                                            Decoration.mark({
                                                class: `ol-cm-command-${text}`,
                                                inclusive: true,
                                            }).range(markFrom, markTo),
                                        );
                                    }
                                }
                            }
                            return;
                        }

                        if (nodeRef.type.is("SectioningCommand")) {
                            const ctrlSeq = nodeRef.node.getChild("$CtrlSeq");
                            if (ctrlSeq) {
                                const text = state.doc.sliceString(ctrlSeq.from + 1, ctrlSeq.to);
                                decorations.push(
                                    Decoration.mark({
                                        class: `ol-cm-heading ol-cm-command-${text}`,
                                    }).range(nodeRef.from, nodeRef.to),
                                );
                            }
                            return;
                        }

                        if (nodeRef.type.is("Caption") || nodeRef.type.is("Label")) {
                            const type = nodeRef.type.is("Caption") ? "caption" : "label";
                            const argument = nodeRef.node.getChild("$Argument");
                            if (!argument) {
                                return;
                            }
                            const start = state.doc.lineAt(nodeRef.from);
                            const end = state.doc.lineAt(nodeRef.to);
                            for (let lineNumber = start.number; lineNumber <= end.number; lineNumber += 1) {
                                const line = state.doc.line(lineNumber);
                                decorations.push(
                                    Decoration.line({
                                        class: `ol-cm-${type}-line`,
                                    }).range(line.from),
                                );
                            }
                            return;
                        }

                        if (nodeRef.type.is("TextColorCommand")) {
                            const result = parseColorArguments(state, nodeRef.node);
                            if (result) {
                                const { color, from: markFrom, to: markTo } = result;
                                decorations.push(
                                    Decoration.mark({
                                        class: "ol-cm-textcolor",
                                        inclusive: true,
                                        attributes: {
                                            style: `color: ${color}`,
                                        },
                                    }).range(markFrom, markTo),
                                );
                            }
                            return;
                        }

                        if (nodeRef.type.is("ColorBoxCommand")) {
                            const result = parseColorArguments(state, nodeRef.node);
                            if (result) {
                                const { color, from: markFrom, to: markTo } = result;
                                decorations.push(
                                    Decoration.mark({
                                        class: "ol-cm-colorbox",
                                        inclusive: true,
                                        attributes: {
                                            style: `background-color: ${color}`,
                                        },
                                    }).range(markFrom, markTo),
                                );
                            }
                            return;
                        }

                        if (nodeRef.type.is("$Environment")) {
                            const environmentName = getUnstarredEnvironmentName(nodeRef.node, state);
                            if (!environmentName) {
                                return;
                            }

                            switch (environmentName) {
                                case "abstract":
                                case "figure":
                                case "table":
                                case "verbatim":
                                case "lstlisting": {
                                    const start = state.doc.lineAt(nodeRef.from);
                                    const end = state.doc.lineAt(nodeRef.to);
                                    const centered = Boolean(centeringNodeForEnvironment(nodeRef));

                                    for (let lineNumber = start.number; lineNumber <= end.number; lineNumber += 1) {
                                        const line = state.doc.line(lineNumber);
                                        const classes = [
                                            `ol-cm-environment-${environmentName}`,
                                            "ol-cm-environment-line",
                                        ];
                                        if (centered) {
                                            classes.push("ol-cm-environment-centered");
                                        }
                                        decorations.push(
                                            Decoration.line({
                                                class: classes.join(" "),
                                            }).range(line.from),
                                        );
                                    }
                                    break;
                                }
                                case "quote":
                                case "quotation":
                                case "quoting":
                                case "displayquote": {
                                    const start = state.doc.lineAt(nodeRef.from);
                                    const end = state.doc.lineAt(nodeRef.to);
                                    for (let lineNumber = start.number; lineNumber <= end.number; lineNumber += 1) {
                                        const line = state.doc.line(lineNumber);
                                        decorations.push(
                                            Decoration.line({
                                                class: [
                                                    `ol-cm-environment-${environmentName}`,
                                                    "ol-cm-environment-quote-block",
                                                    "ol-cm-environment-line",
                                                ].join(" "),
                                            }).range(line.from),
                                        );
                                    }
                                    break;
                                }
                                default:
                                    if (theoremStyles.has(environmentName)) {
                                        const theoremStyle = theoremStyles.get(environmentName);
                                        if (theoremStyle) {
                                            const start = state.doc.lineAt(nodeRef.from);
                                            const end = state.doc.lineAt(nodeRef.to);
                                            decorations.push(
                                                Decoration.line({
                                                    class: [
                                                        `ol-cm-environment-theorem-${theoremStyle}`,
                                                        "ol-cm-environment-first-line",
                                                    ].join(" "),
                                                }).range(start.from),
                                            );

                                            for (
                                                let lineNumber = start.number + 1;
                                                lineNumber <= end.number - 1;
                                                lineNumber += 1
                                            ) {
                                                const line = state.doc.line(lineNumber);
                                                decorations.push(
                                                    Decoration.line({
                                                        class: [
                                                            `ol-cm-environment-theorem-${theoremStyle}`,
                                                            "ol-cm-environment-line",
                                                        ].join(" "),
                                                    }).range(line.from),
                                                );
                                            }

                                            decorations.push(
                                                Decoration.line({
                                                    class: [
                                                        `ol-cm-environment-theorem-${theoremStyle}`,
                                                        "ol-cm-environment-last-line",
                                                    ].join(" "),
                                                }).range(start.from),
                                            );
                                        }
                                    }
                                    break;
                            }
                        }
                    },
                });
            }

            return Decoration.set(decorations, true);
        };

        let previousTree = syntaxTree(view.state);

        return {
            decorations: createDecorations(view.state, previousTree),
            update(update) {
                const tree = syntaxTree(update.state);
                if (tree.type === previousTree.type && tree.length < update.view.viewport.to) {
                    this.decorations = this.decorations.map(update.changes);
                } else if (tree !== previousTree || update.viewportChanged) {
                    previousTree = tree;
                    this.decorations = createDecorations(update.state, tree);
                }
            },
        };
    },
    {
        decorations: (value) => value.decorations,
    },
);
